import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { useDataContext } from "../../hub/data-context-provider.js";
import { CANONICAL_AGENT_V2_SYSTEM_PROMPT_TEMPLATE, V2_DEFAULT_SYSTEM_PROMPT_SETTING_KEY, renderSystemPromptTemplate, } from "./defaultSystemPrompt.js";
import { OpenRouterModelPicker } from "./OpenRouterModelPicker.js";
import { RemoteFilePicker } from "./RemoteFilePicker.js";
const DEFAULT_MODEL = "google/gemma-4-31b-it";
export function CreateAgentV2Dialog({ workspaceId, onClose, onCreated }) {
    const ctx = useDataContext();
    const [hosts, setHosts] = useState(null);
    const [hostId, setHostId] = useState(null);
    const [model, setModel] = useState(DEFAULT_MODEL);
    const [name, setName] = useState("");
    // Workspace-customized system-prompt template — loaded from
    // workspace_settings (key v2DefaultSystemPromptTemplate). Falls
    // back to the canonical hardcoded template until the load
    // resolves so the textarea is never empty mid-render.
    const [promptTemplate, setPromptTemplate] = useState(CANONICAL_AGENT_V2_SYSTEM_PROMPT_TEMPLATE);
    const [systemPrompt, setSystemPrompt] = useState(renderSystemPromptTemplate(CANONICAL_AGENT_V2_SYSTEM_PROMPT_TEMPLATE, {
        agentName: "",
        model: DEFAULT_MODEL,
    }));
    // Tracks whether the user has manually edited the textarea. While
    // false, the systemPrompt re-renders from `promptTemplate` whenever
    // name/model changes (so picking a model updates the "Active model"
    // line live). First manual edit flips this to true and the textarea
    // becomes free-form.
    const [systemPromptDirty, setSystemPromptDirty] = useState(false);
    // KTK-220 — working directory on the runtime host. Picker writes the
    // resolved absolute path back here on "Use this directory."
    const [workingDir, setWorkingDir] = useState("~");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    // Pull the user's registered Agent V2 hosts.
    // KTK-324 — endpoint is now user-scoped (was `?workspace_id=…`).
    // The same host shows up in every workspace's create-agent picker
    // that the user owns. Per-call workspace is threaded through
    // `createAgentV2` below, not at list time.
    useEffect(() => {
        let cancelled = false;
        const hub = ctx.hub;
        if (typeof hub.listAgentRuntimeHosts !== "function") {
            setHosts([]);
            return () => undefined;
        }
        hub
            .listAgentRuntimeHosts()
            .then((list) => {
            if (cancelled)
                return;
            setHosts(list);
            setHostId((prev) => prev ?? list[0]?.host_id ?? null);
        })
            .catch((e) => {
            if (!cancelled) {
                setHosts([]);
                setError(e instanceof Error ? e.message : String(e));
            }
        });
        return () => {
            cancelled = true;
        };
    }, [ctx.hub]);
    // Load the workspace's customized template (falls back to the
    // canonical hardcoded one when the workspace hasn't customized).
    useEffect(() => {
        let cancelled = false;
        ctx.hub
            .getWorkspaceSetting(workspaceId, V2_DEFAULT_SYSTEM_PROMPT_SETTING_KEY)
            .then((v) => {
            if (cancelled)
                return;
            if (typeof v === "string" && v.length > 0) {
                setPromptTemplate(v);
            }
        })
            .catch(() => {
            // Setting not present — keep the canonical fallback.
        });
        return () => {
            cancelled = true;
        };
    }, [ctx.hub, workspaceId]);
    // Re-render the systemPrompt textarea from `promptTemplate` whenever
    // template / name / model changes — UNTIL the user manually edits
    // the textarea (then we leave their content alone).
    useEffect(() => {
        if (systemPromptDirty)
            return;
        setSystemPrompt(renderSystemPromptTemplate(promptTemplate, { agentName: name, model }));
    }, [promptTemplate, name, model, systemPromptDirty]);
    const submitDisabled = submitting ||
        !hostId ||
        !model.trim() ||
        !name.trim() ||
        !workingDir.trim();
    const onSubmit = async () => {
        if (submitDisabled)
            return;
        setSubmitting(true);
        setError(null);
        try {
            const hub = ctx.hub;
            if (typeof hub.createAgentV2 !== "function") {
                throw new Error("createAgentV2 endpoint isn't available on this Hub yet. Update Kritaka or pick the Claude Code tab.");
            }
            // KTK-246 / KTK-297 — the OpenRouter key lives on the workspace
            // row; the runtime reads it at spawn from the agent_start frame.
            // No per-agent api_key in this body.
            const agent = await hub.createAgentV2({
                workspace_id: workspaceId,
                host_id: hostId,
                name: name.trim(),
                model: model.trim(),
                system_prompt: systemPrompt,
                working_dir: workingDir.trim(),
            });
            onCreated(agent);
            // Akari called this out — without dismissing on success the
            // user can re-submit the form, accidentally creating duplicate
            // agents. Mirrors what CreateAgentDialog (Claude Code tab)
            // does in the same spot.
            onClose();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        }
        finally {
            setSubmitting(false);
        }
    };
    return (_jsxs("div", { "data-agent-v2": "", "data-theme": "dark", style: { display: "flex", flexDirection: "column", gap: 14 }, children: [_jsx(Section, { label: "Runtime host", children: hosts === null ? (_jsx(Hint, { muted: true, children: "Loading machines\u2026" })) : hosts.length === 0 ? (_jsx(Hint, { children: "No Agent V2 runtimes registered on this account yet. Install the runtime on your machine via the Kritaka website, sign in, and refresh this dialog." })) : (_jsx("select", { value: hostId ?? "", onChange: (e) => setHostId(e.target.value), style: fieldStyle(), children: hosts.map((h) => (_jsxs("option", { value: h.host_id, children: [h.display_name, " \u00B7 ", h.platform] }, h.host_id))) })) }), _jsxs(Section, { label: "Default model", children: [_jsx(OpenRouterModelPicker, { value: model, onChange: setModel }), _jsxs(Hint, { muted: true, children: ["Default is", " ", _jsx("span", { style: { fontFamily: "var(--mono)", color: "var(--ink-2)" }, children: DEFAULT_MODEL }), " ", "(cheap + capable). Type to fuzzy-search OpenRouter's full catalog."] })] }), _jsxs(Section, { label: "Working directory (on the runtime host)", children: [_jsx("input", { type: "text", value: workingDir, onChange: (e) => setWorkingDir(e.target.value), placeholder: "~", style: fieldStyle("mono") }), hostId && (_jsx(RemoteFilePicker, { hostId: hostId, hostKind: "runtime", workspaceId: workspaceId, initialPath: workingDir || "~", onSelect: setWorkingDir })), !hostId && (_jsx(Hint, { muted: true, children: "Pick a runtime host first \u2014 the file picker browses that machine's filesystem." }))] }), _jsx(Section, { label: "Name", children: _jsx("input", { type: "text", value: name, onChange: (e) => setName(e.target.value), placeholder: "e.g. Atlas", style: fieldStyle() }) }), _jsx(Section, { label: "System prompt", children: _jsx("textarea", { value: systemPrompt, onChange: (e) => {
                        setSystemPrompt(e.target.value);
                        // First manual edit freezes the auto-fill so name/model
                        // changes after this point don't clobber the user's text.
                        if (!systemPromptDirty)
                            setSystemPromptDirty(true);
                    }, rows: 14, style: { ...fieldStyle("mono"), resize: "vertical", minHeight: 280 } }) }), error && (_jsx("div", { style: {
                    padding: "8px 10px",
                    border: "1px solid color-mix(in oklab, var(--accent) 45%, transparent)",
                    borderRadius: "var(--radius-sm)",
                    background: "var(--accent-soft)",
                    color: "var(--accent-ink)",
                    fontSize: "var(--fs-meta)",
                }, children: error })), _jsxs("div", { style: { display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 4 }, children: [_jsx("button", { type: "button", onClick: onClose, style: buttonStyle("ghost"), children: "cancel" }), _jsx("button", { type: "button", disabled: submitDisabled, onClick: onSubmit, style: buttonStyle("primary", submitDisabled), children: submitting ? "creating…" : "create agent" })] })] }));
}
function Section({ label, children }) {
    return (_jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 4 }, children: [_jsx("span", { style: {
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: 0.04,
                    color: "var(--ink-3)",
                    textTransform: "uppercase",
                }, children: label }), children] }));
}
function Hint({ children, muted = false }) {
    return (_jsx("span", { style: { fontSize: "var(--fs-meta)", color: muted ? "var(--ink-4)" : "var(--ink-3)" }, children: children }));
}
function fieldStyle(mono) {
    return {
        width: "100%",
        padding: "6px 10px",
        borderRadius: "var(--radius-sm)",
        border: "1px solid var(--hairline)",
        background: "var(--paper-2)",
        color: "var(--ink)",
        font: "inherit",
        fontFamily: mono === "mono" ? "var(--mono)" : undefined,
        fontSize: "var(--fs-body)",
    };
}
function buttonStyle(variant = "ghost", disabled = false) {
    const base = {
        font: "inherit",
        fontSize: "var(--fs-meta)",
        padding: "6px 14px",
        borderRadius: 999,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        border: "1px solid var(--hairline)",
        background: "var(--paper-2)",
        color: "var(--ink-2)",
    };
    if (variant === "primary") {
        base.background = "var(--accent)";
        base.color = "var(--paper)";
        base.borderColor = "var(--accent)";
    }
    return base;
}
//# sourceMappingURL=CreateAgentV2Dialog.js.map